import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Users, Trophy } from 'lucide-react';
import { Button } from './ui/button';
import GameCanvas from './GameCanvas';

const GameScreen = ({ 
  selectedSkin, 
  selectedNft,
  onBack, 
  onGameOver,
  tokenBalance = 0,
  ownedWeapons = [],
  multiplayerSession = null
}) => {
  const [isLandscape, setIsLandscape] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if this is a multiplayer game
  const isMultiplayer = !!multiplayerSession && !!multiplayerSession.lobby;
  // Backend Lobby model uses `lobby_id` (snake_case); accept legacy `id` as
  // a fallback for safety. A mismatch here silently disables the in-game
  // multiplayer WebSocket (peers invisible) — see useMultiplayerSync warn.
  const lobbyId = multiplayerSession?.lobby?.lobby_id ?? multiplayerSession?.lobby?.id;
  const gameSessionId = multiplayerSession?.sessionId;

  // Check if this is a tournament match
  const isTournament = !!multiplayerSession?.tournamentId;
  const tournamentTimeLimit = multiplayerSession?.timeLimit || 120;

  // Determine player sprite: NFT image takes priority over skin image
  // Convert relative paths to full URLs
  const playerSpriteUrl = useMemo(() => {
    const imageUrl = selectedNft?.image_url || selectedSkin?.image_url || null;
    if (!imageUrl) return null;
    // If it's a relative path, convert to full URL
    if (imageUrl.startsWith('/')) {
      return window.location.origin + imageUrl;
    }
    return imageUrl;
  }, [selectedNft?.image_url, selectedSkin?.image_url]);
  
  const playerColor = selectedNft?.color || selectedSkin?.color || '#FF3B30';

  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
      setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header - Only show on desktop or portrait mobile */}
      {!isMobile && (
        <div className="p-2 sm:p-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="text-[#A1A1AA] hover:text-white text-sm"
              data-testid="game-back-btn"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span>Exit</span>
            </Button>
            
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <span className="text-xs sm:text-sm">Playing as:</span>
              <div 
                className="w-3 h-5 sm:w-4 sm:h-6 rounded"
                style={{ backgroundColor: selectedSkin?.color || '#FF3B30' }}
              />
              <span className="font-game text-[10px] sm:text-xs text-[#14F195]">
                {selectedSkin?.name || 'Classic'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Game Canvas - Full screen on mobile */}
      <div className={`flex-1 ${isMobile ? 'h-screen' : ''}`}>
        <GameCanvas 
          playerColor={playerColor}
          playerSpriteUrl={playerSpriteUrl}
          nftBonuses={selectedNft?.bonuses || null}
          nftName={selectedNft?.name || null}
          nftId={selectedNft?.id || null}
          onGameOver={onGameOver}
          autoStart={true}
          tokenBalance={tokenBalance}
          ownedWeapons={ownedWeapons}
          isMultiplayer={isMultiplayer}
          lobbyId={lobbyId}
          gameSessionId={gameSessionId}
          multiplayerLobby={multiplayerSession?.lobby}
          isTournament={isTournament}
          tournamentTimeLimit={tournamentTimeLimit}
        />
      </div>

      {/* Multiplayer indicator */}
      {isMultiplayer && (
        <div className="fixed top-2 right-2 z-50 px-3 py-1 rounded-full bg-[#9945FF]/80 border border-[#9945FF] text-white text-xs font-game flex items-center gap-2">
          <Users className="w-3 h-3" />
          MULTIPLAYER
        </div>
      )}

      {/* Tournament indicator */}
      {isTournament && (
        <div className="fixed top-2 right-2 z-50 px-3 py-1 rounded-full bg-[#FFD700]/80 border border-[#FFD700] text-black text-xs font-game flex items-center gap-2">
          <Trophy className="w-3 h-3" />
          TOURNAMENT MATCH
        </div>
      )}

      {/* Floating back button for mobile */}
      {isMobile && (
        <button
          onClick={onBack}
          className="fixed top-2 left-2 z-50 p-2 rounded-full bg-black/30 border border-[#A1A1AA]/30 text-[#A1A1AA]/70"
          data-testid="mobile-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default GameScreen;
